import { useDashboardReview } from '../../hooks/useDashboardReview'
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import keys from 'constants/keys'
import { RootState } from 'constants/interfaces'
import { AppDispatch } from 'config/redux'
import { getAllResults } from 'redux/actions/tasks_v2'
import { isRtlSelector } from 'redux/selectors/general'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button, Chip, HorizontalScroller } from 'components/ui'
import { CollapsibleContent } from 'components/TasksV2/UI/CollapsibleContent'
import { ReasonsCollapsible } from 'components/TasksV2/UI/ReasonsCollapsible'
import { SkipSubtaskButton } from 'components/TasksV2/UI/SkipSubtaskButton'
import { useTranslateDefaultTitle } from 'components/TasksV2/hooks/useTranslateDefaultTitle'
import { useExecutionWorkspace } from '../../hooks/useExecutionWorkspace'
import { useWorkspaceText } from '../../hooks/useWorkspaceText'
import { isMediaAnswer, hasExecutionResult } from '../../model/execution'
import { TaskOverviewContentType } from '../../TaskOverviewStackProvider'
import { ExecutionSubtaskNavigation } from '../ExecutionSubtaskNavigation'
import { ExecutionMediaEntry } from '../ExecutionMediaEntry'
import { ExecutionCompleteState } from '../ExecutionCompleteState'
import { EmptyReviewState } from '../DashboardReview/EmptyReviewState'
import { ExecutionAnswer } from '../ExecutionAnswer'
import { ExecutionSubtaskCard, useSubtaskTypeName } from '../ExecutionSubtaskCard'
import { SubtaskIcon } from '../ExecutionSubtaskCard/SubtaskIcon'
import styles from './styles.module.css'

function Title({ title }: { title: string }) {
  return <strong>{useTranslateDefaultTitle(title)}</strong>
}

export function ExecutionWorkspace({
  active = true,
  headerSlot,
  navigationSlot,
}: {
  active?: boolean
  headerSlot?: HTMLDivElement | null
  navigationSlot?: HTMLDivElement | null
}) {
  const state = useExecutionWorkspace()
  const review = useDashboardReview()
  const { subtasks, selected, selectedId, task, busy, isReadMode, resultFor, completed } = state
  const t = useWorkspaceText()
  const rtl = useSelector(isRtlSelector)
  const pollLoading = useSelector((state: RootState) => state.tasks_v2.allResultsLoading)
  const dispatch: AppDispatch = useDispatch()
  const [category, setCategory] = useState('')
  const list = useRef<HTMLDivElement>(null)
  const focusRequest = useRef({ selectedId, active: false, pending: false })
  const categories = Array.from(new Set(subtasks.map((subtask) => subtask.category).filter(Boolean)))
  const position = subtasks.findIndex((subtask) => subtask.st_id === selectedId)
  const result = selected ? resultFor(selected.st_id) : undefined
  const typeName = useSubtaskTypeName()
  const allDone = subtasks.length > 0 && completed.size === state.allSubtasks.length && !state.hasUnsavedAnswers
  const completeDisabled = busy || state.hasUnsavedAnswers || !!state.remaining.length || !subtasks.length
  const completeAction = !isReadMode && (
    <Button
      variant="primary"
      h="36px"
      fontSize="13px"
      px="14px"
      isDisabled={completeDisabled}
      onClick={state.closeContent}
    >
      {t.complete}
    </Button>
  )
  const navigation = (
    <>
      {(task.type !== keys.TASK_V2_TYPES.VM_TASK || (selected && !isMediaAnswer(selected))) && subtasks.length > 0 && (
        <ExecutionSubtaskNavigation
          onSelect={state.choose}
          position={position + 1}
          total={subtasks.length}
          isBusy={busy}
          hasPrev={position > 0}
          hasNext={position < subtasks.length - 1}
          goPrev={() => state.choose(subtasks[position - 1].st_id)}
          goNext={() => state.choose(subtasks[position + 1].st_id)}
        />
      )}
    </>
  )

  useEffect(() => {
    const ids = subtasks
      .filter((subtask) => [keys.SUBTASK_TYPES.POLL_SUBTASK, keys.SUBTASK_TYPES.PRIVATE_POLL].includes(subtask.type))
      .map((subtask) => subtask.st_id)
    if (ids.length) dispatch(getAllResults({ task, subtasksIds: ids }))
  }, [dispatch, subtasks, task])

  useLayoutEffect(() => {
    const previous = focusRequest.current
    const shouldFocus = !previous.active || previous.selectedId !== selectedId || previous.pending
    focusRequest.current = { selectedId, active, pending: false }
    if (!active || !selectedId || !shouldFocus) return

    // The full-screen pager can select a card outside the overview's current filter.
    // Clear that filter on return/selection, while preserving deliberate filter browsing.
    if (category && selected && selected.category !== category) {
      focusRequest.current.pending = true
      setCategory('')
      return
    }
    const card = list.current?.querySelector<HTMLButtonElement>('[data-subtask-id][aria-pressed="true"]')
    card?.focus({ preventScroll: true })
  }, [active, selectedId, selected, category])

  return (
    <div
      className={styles.workspace}
      dir={rtl ? 'rtl' : 'ltr'}
      data-execution-workspace
      data-all-done={allDone && !selected}
      data-answer-open={!!selected && !isMediaAnswer(selected)}
    >
      {active && (
        <>
          {navigationSlot ? createPortal(navigation, navigationSlot) : navigation}
          {headerSlot ? createPortal(completeAction, headerSlot) : completeAction}
        </>
      )}
      <div className={styles.columns}>
        <section className={styles.list}>
          <div className={styles.listScroll} ref={list}>
            {task.contents?.map((content, index) => (
              <CollapsibleContent
                key={content.index_ref || index}
                {...content}
                text={content.text || undefined}
                images={content.images || undefined}
                videos={content.videos || undefined}
                files={content.files || undefined}
                variant="inset"
              />
            ))}
            <div className={styles.listToolbar}>
              <div className={styles.toolbarHeading}>
                {!!categories.length && !review && <span className={styles.categoryLabel}>{t.categories}</span>}
                <span className={styles.subtaskCount}>
                  {subtasks.length === 1 ? t.oneSubtask : t.subtaskCount.replace('{count}', String(subtasks.length))}
                </span>
              </div>
              {!!categories.length && !review && (
                <div className={styles.categoryFilters} role="group" aria-label={t.categories}>
                  <HorizontalScroller gap="8px" fadeSize={24} className={styles.categoryTags}>
                    {[
                      { value: '', label: t.all, count: subtasks.length },
                      ...categories.map((value) => ({
                        value,
                        label: value,
                        count: subtasks.filter((subtask) => subtask.category === value).length,
                      })),
                    ].map(({ value, label, count }) => {
                      const selected = category === value
                      return (
                        <Chip
                          key={value}
                          label={
                            <>
                              {label}
                              <span className={styles.filterCount}>{count}</span>
                            </>
                          }
                          aria-label={label}
                          size="md"
                          color={selected ? '#ffffff' : '#22272a'}
                          bg={selected ? '#22272a' : '#eef0f2'}
                          border="1px solid transparent"
                          _hover={{ bg: selected ? '#3b4247' : '#e1e5e8' }}
                          _focus={{ boxShadow: '0 0 0 3px #aab1b7', outline: 'none' }}
                          aria-pressed={selected}
                          onClick={() => setCategory(value)}
                        />
                      )
                    })}
                  </HorizontalScroller>
                </div>
              )}
            </div>
            <div className={styles.subtaskRows}>
              {subtasks
                .filter((subtask) => !category || subtask.category === category)
                .map((subtask) => (
                  <ExecutionSubtaskCard
                    key={subtask.st_id}
                    subtask={subtask}
                    result={resultFor(subtask.st_id)}
                    selected={subtask.st_id === selectedId}
                    disabled={busy}
                    readOnly={isReadMode}
                    layout="row"
                    actionLabel={isReadMode ? t.view : t.start}
                    onSelect={() => state.choose(subtask.st_id)}
                  />
                ))}
            </div>
          </div>
        </section>
        <section className={styles.editor}>
          {selected ? (
            <>
              <header>
                <Button
                  variant="icon"
                  type="button"
                  className={styles.back}
                  onClick={() => state.choose(null)}
                  isDisabled={busy}
                  aria-label={t.back}
                >
                  {rtl ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
                </Button>
                <SubtaskIcon subtask={selected} />
                <div>
                  <Title title={selected.title} />
                  <small>{typeName(selected)}</small>
                </div>
              </header>
              {isReadMode && !hasExecutionResult(selected, result) && !isMediaAnswer(selected) ? (
                <EmptyReviewState media={false} />
              ) : (
                <>
                  <div
                    className={`${styles.editorScroll}${
                      isMediaAnswer(selected)
                        ? ` ${styles.mediaEntryContainer}`
                        : selected.type === keys.SUBTASK_TYPES.OPEN_QUESTION
                        ? ` ${styles.questionEditor}`
                        : ''
                    }`}
                    key={selected.st_id}
                  >
                    {isMediaAnswer(selected) ? (
                      <ExecutionMediaEntry
                        isVideo={selected.type === keys.SUBTASK_TYPES.VIDEO_SUBTASKS}
                        disabled={busy}
                        onOpen={() => state.choose(selected.st_id)}
                      />
                    ) : (
                      <>
                        {selected.content && (
                          <section className={styles.instructions}>
                            <small>{t.what}</small>
                            <CollapsibleContent {...selected.content} showHeader={false} />
                          </section>
                        )}
                        {!!result?.reservations?.reasons.length && (
                          <ReasonsCollapsible
                            reasons={result.reservations.reasons}
                            variant={
                              result.status === keys.TASK_STATUSES.SKIPPED.value
                                ? 'skipped'
                                : result.status === keys.TASK_STATUSES.DISAPPROVED.value
                                ? 'rejected'
                                : 'warning'
                            }
                            defaultExpanded={false}
                          />
                        )}
                        <div
                          className={
                            selected.type === keys.SUBTASK_TYPES.OPEN_QUESTION
                              ? styles.centeredQuestionAnswer
                              : undefined
                          }
                        >
                          <ExecutionAnswer
                            subtask={selected}
                            draft={state.draft}
                            onChange={state.change}
                            disabled={busy || isReadMode || (state.isPollSelection && pollLoading)}
                            result={result}
                          />
                        </div>
                        {selected.type === keys.SUBTASK_TYPES.POLL_SUBTASK && (
                          <Button
                            variant="ghost"
                            isDisabled={pollLoading}
                            onClick={() =>
                              state.openContent(TaskOverviewContentType.VOTES_OVERVIEW, {
                                title: selected.title,
                                selectedSubtask: selected,
                              })
                            }
                          >
                            {t.votes}
                          </Button>
                        )}
                      </>
                    )}
                    {!isMediaAnswer(selected) && (
                      <SkipSubtaskButton
                        isRequired={selected.is_required}
                        isSkipped={result?.status === keys.TASK_STATUSES.SKIPPED.value}
                        isResult={completed.has(selected.st_id)}
                        subtaskType={selected.type}
                        tid={task.tid}
                        st_id={selected.st_id}
                        resultContextId={state.resultContextId}
                      />
                    )}
                  </div>
                  {!isMediaAnswer(selected) && !isReadMode && (
                    <footer>
                      <small role={state.error ? 'alert' : undefined}>
                        {state.error
                          ? t.saveError
                          : busy
                          ? t.saving
                          : state.isPollSelection
                          ? ''
                          : !state.canDone
                          ? t.requiredHint
                          : !selected.is_required
                          ? t.optionalHint
                          : ''}
                      </small>
                      {state.isPollSelection ? (
                        state.pollNeedsRetry && (
                          <Button variant="secondary" isDisabled={busy || pollLoading} onClick={state.retryPoll}>
                            {t.retry}
                          </Button>
                        )
                      ) : (
                        <Button variant="secondary" isDisabled={busy || !state.canDone} onClick={state.done}>
                          {t.done}
                        </Button>
                      )}
                    </footer>
                  )}
                </>
              )}
            </>
          ) : allDone ? (
            <ExecutionCompleteState action={completeAction} />
          ) : (
            <div className={styles.empty}>{t.select}</div>
          )}
        </section>
      </div>
    </div>
  )
}
